// dependency-graph-ignore-file
import { vec2, type ReadonlyVec2 } from 'gl-matrix';

import {
  approximateCubicBezierBoundingBox,
  getCubicBezierBoundingBox,
} from '@canvas/bezier';
import {
  boxCreate,
  boxExpand,
  boxExpandToFitBox,
  boxExpandToFitPoint,
  boxIsUnionReset,
  boxSize,
  boxUnionReset,
  type Box,
} from '@canvas/math/box';
import { boxContainsBox } from '@canvas/math/box/intersect';

import { type Graphic } from './graphic';
import { graphicSpaceToWorldSpaceMatrix } from './math';
import { GraphicCubicBezier, GraphicLine, type GraphicSection } from './types';

// when using this flyweight, always use the result value immediately
// so its free to be overwritten by the next usage of bezierBoxFlyweight
const bezierBoxFlyweight = boxCreate();

/**
 * Gets the bounding box of a graphic section in world space.
 *
 * @param section the graphic section to get the bounding box for
 * @param graphicBoundingBox the bounding box of the graphic in world space
 * @param rotation the rotation of the graphic in radians
 * @param rotationOrigin the origin of the rotation in world space
 * @param strokeThickness if provided, the thickness is used for all strokes
 * @returns the bounding box of the graphic section in world space
 */
const getGraphicSectionBoundingBox = (
  section: GraphicSection,
  graphicBoundingBox: Box,
  rotation?: number,
  rotationOrigin?: ReadonlyVec2,
  strokeThickness?: number
) => {
  const graphicSize = boxSize(graphicBoundingBox);

  const { segments } = section;

  // If the section is empty, return undefined
  if (segments.length === 0) {
    return undefined;
  }

  // Get the transform to convert the normalised graphic points to world space
  // coordinates with the given rotation and rotation origin
  const transform = graphicSpaceToWorldSpaceMatrix(
    graphicBoundingBox,
    rotation,
    rotationOrigin
  );

  // Initialize the bounding box with a union reset
  const boundingBox = boxUnionReset(boxCreate());

  // Grow the bounding box to fit lines
  for (const segment of segments) {
    if (segment instanceof GraphicLine) {
      // Get the line points in rotated world space coordinates
      const start = segment.start.resolveToNormalisedScaleWithTransform(
        vec2.create(),
        graphicSize,
        transform
      );
      const end = segment.end.resolveToNormalisedScaleWithTransform(
        vec2.create(),
        graphicSize,
        transform
      );

      // Expand the bounding box to fit the line
      boxExpandToFitPoint(boundingBox, start);
      boxExpandToFitPoint(boundingBox, end);
    }
  }

  // Grow the bounding box to fit bezier curves
  // This is done in a second loop because getCubicBezierBoundingBox
  // is a heavier calculation than lines, and it can be skipped
  // if the bounding box is already known to be around the bezier curve
  for (const segment of segments) {
    if (segment instanceof GraphicCubicBezier) {
      // Get the bezier points in rotated world space coordinates
      const bezier = segment.resolveToNormalisedScaleWithTransform(
        graphicSize,
        transform
      );

      // If an approximation of the bezier is fully inside the current bounding box
      // then this bezier won't make the bounding box any bigger, so skip it
      const approxBox = approximateCubicBezierBoundingBox(bezier, bezierBoxFlyweight);
      const couldAffectBoundingBox = !boxContainsBox(boundingBox, approxBox);
      if (couldAffectBoundingBox) {
        // Expand the bounding box to fit the bezier
        const accurateBox = getCubicBezierBoundingBox(bezier, bezierBoxFlyweight);
        boxExpandToFitBox(boundingBox, accurateBox);
      }
    }
  }

  // if no boxes were added to the union, return undefined
  if (boxIsUnionReset(boundingBox)) {
    return;
  }

  // if strokes have thickness, expand bounding box size by half on all sides
  // in world space to account for these
  if (strokeThickness) {
    boxExpand(boundingBox, strokeThickness * 0.5, boundingBox);
  }

  return boundingBox;
};

/**
 * Gets the bounding box of a graphic in world space.
 *
 * @param graphic the graphic to get the bounding box for
 * @param graphicBoundingBox the bounding box of the graphic in world space
 * @param rotation the rotation of the graphic in radians
 * @param rotationOrigin the origin of the rotation in world space
 * @param strokeThickness if provided, the thickness is used for all strokes
 * @returns the bounding box of the graphic in world space
 */
export const getGraphicBoundingBox = (
  graphic: Graphic,
  graphicBoundingBox: Box,
  rotation?: number,
  rotationOrigin?: ReadonlyVec2,
  strokeThickness?: number
): Box | undefined => {
  // create a box for collecting a union onto
  const boundingBox = boxUnionReset(boxCreate());

  // add relevant fills bounding boxes to the union
  for (const section of graphic.fills) {
    const box = getGraphicSectionBoundingBox(
      section,
      graphicBoundingBox,
      rotation,
      rotationOrigin
    );
    if (box) {
      boxExpandToFitBox(boundingBox, box);
    }
  }

  // add relevant strokes bounding boxes to the union
  for (const section of graphic.strokes) {
    const box = getGraphicSectionBoundingBox(
      section,
      graphicBoundingBox,
      rotation,
      rotationOrigin,
      strokeThickness
    );
    if (box) {
      boxExpandToFitBox(boundingBox, box);
    }
  }

  // if no boxes were added to the union, return undefined
  if (boxIsUnionReset(boundingBox)) {
    return;
  }

  // return the union of all boxes
  return boundingBox;
};

/**
 * Checks if a selection box contains the bounding box of a given graphic
 * section in world space.
 *
 * @param selectionBox the bounding box of the selection in world space
 * @param graphicBoundingBox the bounding box of the graphic in world space
 * @param section the graphic section to check
 * @param rotation the rotation of the graphic in radians (optional)
 * @param rotationOrigin the origin of the rotation in world space (optional)
 * @returns true if the selection box contains the graphic section bounding box,
 * false otherwise
 */
export const boxContainsGraphicSectionBoundingBox = (
  selectionBox: Box,
  graphicBoundingBox: Box,
  section: GraphicSection,
  rotation?: number,
  rotationOrigin?: ReadonlyVec2
) => {
  const sectionBoundingBox = getGraphicSectionBoundingBox(
    section,
    graphicBoundingBox,
    rotation,
    rotationOrigin
  );

  return sectionBoundingBox ? boxContainsBox(selectionBox, sectionBoundingBox) : false;
};

/**
 * Checks if a selection box contains any of the bounding boxes of the graphic
 * sections in world space. (Returns true as soon as any section is found)
 *
 * This is useful when checking intersection between a selection box and a
 * graphic as it is possible for graphics to have multiple sections that are
 * separated by non-intersecting / transparent areas.
 *
 * @param selectionBox the bounding box of the selection in world space
 * @param graphicBoundingBox the bounding box of the graphic in world space
 * @param graphic the graphic to check
 * @param rotation the rotation of the graphic in radians (optional)
 * @param rotationOrigin the origin of the rotation in world space (optional)
 * @returns true if the selection box contains any of the graphic sections
 * bounding boxes, false otherwise
 */
export const boxContainsAnyGraphicSectionBoundingBox = (
  selectionBox: Box,
  graphicBoundingBox: Box,
  graphic: Graphic,
  rotation?: number,
  rotationOrigin?: ReadonlyVec2
) => {
  // Check if the selection box contains any of the fill sections
  for (const section of graphic.fills) {
    if (
      boxContainsGraphicSectionBoundingBox(
        selectionBox,
        graphicBoundingBox,
        section,
        rotation,
        rotationOrigin
      )
    ) {
      return true;
    }
  }

  // Check if the selection box contains any of the stroke sections
  for (const section of graphic.strokes) {
    if (
      boxContainsGraphicSectionBoundingBox(
        selectionBox,
        graphicBoundingBox,
        section,
        rotation,
        rotationOrigin
      )
    ) {
      return true;
    }
  }

  return false;
};
